'use client'

import { Heart } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Badge } from '~/components/ui/catalyst/badge'
import { Button } from '~/components/ui/catalyst/button'
import { Divider } from '~/components/ui/catalyst/divider'
import { LazyImageDithering } from './lazy-image-dithering'

interface TechIcon {
  name: string
  color?: string
}

interface DisabledSpecCardProps {
  id: string
  title: string
  description: string
  imagePath: string
  techIcons: TechIcon[]
}

export function DisabledSpecCard({ id, title, description, imagePath, techIcons }: DisabledSpecCardProps) {
  const [voteCount, setVoteCount] = useState<number>(0)
  const [hasVoted, setHasVoted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true)

  const fetchVoteData = async () => {
    try {
      const response = await fetch(`/api/tech-spec-votes?specId=${id}`)
      if (response.ok) {
        const data = await response.json()
        setVoteCount(data.voteCount)
        setHasVoted(data.hasVoted)
      }
    }
    catch (error) {
      console.error('Error fetching vote data:', error)
    }
    finally {
      setIsInitialLoading(false)
    }
  }

  const handleVote = async () => {
    setIsLoading(true)

    try {
      const response = await fetch('/api/tech-spec-votes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ specId: id }),
      })

      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Войдите в аккаунт, чтобы голосовать за направления')
          return
        }
        throw new Error('Failed to vote')
      }

      const data = await response.json()

      // Обновляем локальное состояние
      if (data.action === 'added') {
        setVoteCount(prev => prev + 1)
        setHasVoted(true)
        toast.success('Голос учтён! Спасибо за ваш интерес к этому направлению')
      }
      else {
        setVoteCount(prev => Math.max(0, prev - 1))
        setHasVoted(false)
        toast.info('Голос удалён. Вы можете проголосовать снова в любое время')
      }
    }
    catch (error) {
      console.error('Error voting:', error)
      toast.error('Не удалось проголосовать. Попробуйте позже')
    }
    finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchVoteData()
  }, [id])

  return (
    <div
      className="relative overflow-hidden rounded-(--radius) bg-white py-0"
    >
      <div className="relative flex h-[240px] w-full items-center justify-center overflow-hidden bg-neutral-200">
        <Badge color="amber" className="absolute top-4 right-4 z-20 bg-white/85">
          Скоро
        </Badge>
        <div className="absolute inset-0 z-10 size-full bg-linear-to-t from-white from-5% to-transparent to-90%" />
        <LazyImageDithering imagePath={imagePath} />
      </div>

      <div className="space-y-4 p-4">
        <div className="space-y-2">
          <div className="text-2xl font-semibold text-neutral-700">
            {title}
          </div>
          <div className="text-sm text-neutral-600">
            {description}
          </div>
        </div>

        <Divider />

        {/* Tech Stack */}
        <div className="">
          <div className="mb-2 text-xs font-semibold tracking-wide text-neutral-600 uppercase">
            Технологии
          </div>
          <div className="relative">
            <div className="flex gap-2 overflow-x-auto scrollbar-hide">
              {techIcons.map((icon) => {
                return (
                  <Badge
                    key={icon.name}
                    color="zinc"
                    className="shrink-0 border bg-white"
                  >
                    <span className="whitespace-nowrap">{icon.name}</span>
                  </Badge>
                )
              })}
            </div>
            <div className="pointer-events-none absolute top-0 right-0 h-full w-12 bg-gradient-to-l from-white to-transparent" />
          </div>
        </div>

        {/* Vote Section */}
        <div className="flex items-center justify-between gap-4 border-t pt-4">
          <div className="flex items-center gap-2">
            <Heart className="size-5 text-neutral-400" />
            <div className="text-sm">
              <span className="font-semibold text-neutral-900">{voteCount}</span>
              {' '}
              <span className="text-neutral-600">
                {voteCount === 1 ? 'голос' : voteCount >= 2 && voteCount <= 4 ? 'голоса' : 'голосов'}
              </span>
            </div>
          </div>

          <Button
            color={hasVoted ? 'sky' : 'zinc'}
            onClick={handleVote}
            disabled={isLoading || isInitialLoading}
            className="items-center transition-all"
          >
            {isInitialLoading
              ? (
                  <>
                    <Heart className="size-4 animate-pulse" />
                    Загрузка...
                  </>
                )
              : hasVoted
                ? (
                    <>
                      <Heart className="size-4 fill-current" />
                      Проголосовано
                    </>
                  )
                : (
                    <>
                      <Heart className="size-4" />
                      Интересует
                    </>
                  )}
          </Button>
        </div>
      </div>
    </div>
  )
}
